import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';

import { CustomEventService } from '@shared/services/custom-event.service';

import ActionTypesEnum from '@shared/enums/action-constant.enum';

import {
  createFormControl,
  exceptLastValidator,
  uniqueArrayValidator,
  validateFormGroup
} from '@shared/utils/form.util';
import { generateUsername, replaceTrToEn } from '@shared/utils/generate-username.util';

import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { fromJS } from 'immutable';
import { UserService } from "@core/services/user.service";
import { PermissionService } from "@core/services/permission.service";
import { PropertiesService } from "@core/services/properties.service";

@UntilDestroy()
@Component({
  selector: 'app-add-developer-modal',
  templateUrl: './add-developer-modal.component.html'
})
export class AddDeveloperModalComponent implements OnInit, OnDestroy {

  @Input() mode: string;
  @Input() data: Developer.DeveloperModel;
  @Input() teams: Team.TeamModel[];
  @Input() fields: Developer.FieldsModel;
  @Input() loading: boolean;
  nameLowerCase = '';
  surnameLowerCase = '';

  changedUserName = false;

  addDeveloperForm: FormGroup;

  formSubmitted = false;

  constructor(
    private fb: FormBuilder,
    private userService: UserService,
    public permissionService: PermissionService,
    public propertiesService: PropertiesService,
    private customEventService: CustomEventService)
  {
  }

  ngOnInit(): void {
    this.createForm();

    if (this.mode !== 'edit') {
      this.addDeveloperForm.get('name').valueChanges.pipe(
        untilDestroyed(this)
      ).subscribe(item => {
        if (this.changedUserName === false) {
          this.nameLowerCase = replaceTrToEn(item);
          this.addDeveloperForm.get('userName').setValue(generateUsername(this.nameLowerCase, this.surnameLowerCase));
        }
      });

      this.addDeveloperForm.get('surname').valueChanges.pipe(
        untilDestroyed(this)
      ).subscribe(item => {
        if (this.changedUserName === false) {
          this.surnameLowerCase = replaceTrToEn(item);
          this.addDeveloperForm.get('userName').setValue(generateUsername(this.nameLowerCase, this.surnameLowerCase));
        }
      });

      this.addDeveloperForm.get('userName').valueChanges.pipe(
        untilDestroyed(this)
      ).subscribe(item => {
        if (generateUsername(this.nameLowerCase, this.surnameLowerCase) !== item) {
          this.changedUserName = true;
        }
      });
    }
  }

  get accounts(): FormArray {
    return this.addDeveloperForm.get('addonAccounts') as FormArray;
  }

  validateAccounts(): boolean {

    return this.accounts.controls.every(control => {

      if (control.invalid || !control.value) {
         control.markAsDirty();

         if (!control.value) {
           control.setErrors({required: true});
        }
      }

      setTimeout(_ => {
        control.updateValueAndValidity();
      }, 350);

      // in order to add a new input all previous inputs should be filled
      return control.valid && control.value;
    });
  }

  addAccountItem(value?: string): void {

    if(this.validateAccounts() || this.addDeveloperForm.get('addonAccounts').value.includes('')) {
      this.accounts.push(createFormControl(value, {validators: [Validators.required, Validators.pattern(/^(\s*)(?!\s+$).+/)]}));
    }
  }

  removeAccountItem(index): void {
    if (this.accounts.length > 1) {
      this.accounts.removeAt(index);
    }
  }

  createForm(): void {
    const { id, name, surname, userName, teams = [], addonAccounts, companyRole } = this.data;

    this.addDeveloperForm = this.fb.group({
      name: [name, [Validators.required]],
      surname: [surname, [Validators.required]],
      userName: [userName, [Validators.required]],
      id: [id, []],
      teams: [teams.map(team => team.id), []],
      companyRole: [companyRole, [Validators.required]],
      addonAccounts: this.fb.array([], {validators: [Validators.required, uniqueArrayValidator, exceptLastValidator,Validators.pattern(/^(\s*)(?!\s+$).+/)]})
    });

    if (addonAccounts && addonAccounts.length) {
      for ( const item of addonAccounts ) {
        this.addAccountItem(item);
      }
    } else {
      this.addAccountItem(null);
    }
  }

  submitForm(): void {
    this.formSubmitted = true;
    validateFormGroup(this.addDeveloperForm);

    if (this.addDeveloperForm.invalid) {
      this.formSubmitted = false;
      return;
    }

    // filter removes empty keys of the map updateIn updates the list by filtering out null values
    const formData: any = fromJS(this.addDeveloperForm.value)
      .updateIn(['addonAccounts'], item => item.filter(listItem => listItem))
      .filter(val => val);

    this.customEventService.announceEvent(this.mode === 'add' ? ActionTypesEnum.CREATE_DEVELOPER : ActionTypesEnum.UPDATE_DEVELOPER, formData);
  }

  resetForm(): void {
    this.createForm();
  }

  cancelForm(): void {
    this.customEventService.announceEvent(ActionTypesEnum.CLOSE_MODAL);
  }

  ngOnDestroy(): void {
    // prevent memory leak when component destroyed
  }
}

